import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, Upload, LogOut, Heart, Clock, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) {
    return null;
  }

  const menuItems = [
    {
      icon: ExternalLink,
      label: 'See Public Profile',
      description: 'View your profile as others see it',
      link: user.user_url,
      external: false
    },
    {
      icon: Settings,
      label: 'Profile Settings',
      description: 'Edit your name, bio, and profile picture',
      link: '/settings',
      external: false
    },
    {
      icon: Upload,
      label: 'Upload a Photo',
      description: 'Share your photography with the world',
      link: '/upload',
      external: false
    },
    {
      icon: Heart,
      label: 'My Liked Photos',
      description: 'Photos you have liked',
      link: '/my-likes',
      external: false
    },
    {
      icon: Clock,
      label: 'Pending Photos',
      description: 'Photos awaiting approval',
      link: '/my-pending',
      external: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center space-x-4">
            <img
              src={user.user_image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.user_display_name)}&size=80`}
              alt={`${user.user_display_name} Photographer on Explora Mauritius`}
              className="w-20 h-20 rounded-full object-cover"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{user.user_display_name}</h1>
              <p className="text-gray-500">{user.user_email}</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
          {menuItems.map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className="flex items-center p-4 hover:bg-gray-50 transition-colors"
              data-testid={`account-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                <item.icon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{item.label}</p>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            </Link>
          ))}
          
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center p-4 w-full hover:bg-red-50 transition-colors text-left"
            data-testid="account-logout"
          >
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4">
              <LogOut className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-red-600">Logout</p>
              <p className="text-sm text-gray-500">Sign out of your account</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
